// @ts-strict-ignore
import React, { ForwardedRef, forwardRef, memo, useMemo } from 'react'
import { Card, Typography } from '@mui/material'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import { conditionalClassList } from 'services/styles'
import { makeStyles } from 'tss-react/mui'
import { keyframes } from 'tss-react'

const useStyles = makeStyles()(theme => ({
  container: {
    marginTop: 0,
    animation: `${keyframes`
      from {
        transform: rotate3d(1, 0, 0, 45deg);
        opacity: 0;
      }
      to {
        transform: rotate3d(1, 0, 0, 0deg);
        opacity: 1;
      }
    `} 0.5s ease-out`,
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  },
  placeholder: {
    textAlign: 'center',
    padding: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(8),
      paddingTop: theme.spacing(12),
    },
  },
  icon: {
    margin: theme.spacing(2),
    textAlign: 'center',
    '& > *': {
      width: '48px !important',
      height: '48px !important',
    },
  },
  title: {
    margin: theme.spacing(2),
    textAlign: 'center',
    lineHeight: '1.5em',
    verticalAlign: 'middle',
  },
  subtitle: {
    color: 'rgba(0, 0, 0, 0.6)',
    margin: theme.spacing(2),
    textAlign: 'center',
    lineHeight: '1.5em',
    verticalAlign: 'middle',
  },
  actionSeparator: {
    height: 12,
  },
  action: {
    textAlign: 'center',
    margin: theme.spacing(2),
  },
  actionSpacer: {
    height: 24,
    [theme.breakpoints.up('md')]: {
      height: 48,
    },
  },
  noBorder: {
    border: 'none !important',
  },
}))

interface IPagePlaceholderProps {
  name?: string
  title?: React.ReactNode | string
  subtitle?: React.ReactNode | string
  action?: React.ReactNode
  raised?: boolean
  flat?: boolean
  noBorder?: boolean
  centered?: boolean
  icon?: React.ReactNode
  style?: React.CSSProperties
  children?: any
}

function PagePlaceholder(props: IPagePlaceholderProps, ref: ForwardedRef<HTMLDivElement>) {
  const {
    name = 'page-placeholder',
    raised = true,
    flat = false,
    title,
    subtitle,
    icon = <InfoIcon />,
    style,
    noBorder = false,
    centered = false,
    action,
    children,
  } = props
  const { classes } = useStyles()

  const elevation = useMemo<number>(() => {
    if (flat) {
      return 0
    } if (raised) {
      return 7
    }

    return undefined
  }, [flat, raised])

  const variant = useMemo(() => (
    flat ? 'outlined' : 'elevation'
  ), [flat])

  const className = useMemo(() => (
    conditionalClassList({
      [classes.container]: true,
      [classes.noBorder]: noBorder,
      [classes.centered]: centered,
    })
  ), [noBorder, centered])

  return (
    <Card
      ref={ref}
      className={className}
      raised={!flat && raised}
      variant={variant}
      elevation={elevation}
      style={style}
    >
      <div className={classes.placeholder} data-cy={name}>
        {(title || subtitle) && (
          <div className={classes.icon}>
            {icon}
          </div>
        )}
        {title && (
        <Typography variant="h3" className={classes.title}>
          {title}
        </Typography>
        )}
        {subtitle && (
          <Typography variant="subtitle1" className={classes.subtitle}>
            {subtitle}
          </Typography>
        )}
        {children}
        {((title || subtitle) && action) && (
          <div className={classes.actionSeparator} />
        )}
        {action ? (
          <div className={classes.action}>
            {action}
          </div>
        ) : (
          <div className={classes.actionSpacer} />
        )}
      </div>
    </Card>
  )
}

export default memo(forwardRef(PagePlaceholder))
